import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {LinkStyle} from '@ui/buttons/external-link'; 
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '@ui/i18n/trans';
import {FormCheckbox} from '@ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '@ui/settings/use-settings';
import {useContext, useEffect, useState} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {IconButton} from '@ui/buttons/icon-button';
import {VisibilityIcon} from '@ui/icons/material/Visibility';
import {VisibilityOffIcon} from '@ui/icons/material/VisibilityOff';
import {TextField} from '@ui/forms/input-field/text-field/text-field';
import {AirplaneTicketIcon} from '@ui/icons/material/AirplaneTicket';
import {KeyboardArrowDownIcon} from '@ui/icons/material/KeyboardArrowDown';
import {KeyboardArrowUpIcon} from '@ui/icons/material/KeyboardArrowUp';
import {usePreviewRegisterVoucher, PreviewRegisterVoucherResponse} from '../requests/use-preview-register-voucher';
import {toast} from '@ui/toast/toast';
import {useNavigate} from '../../ui/navigation/use-navigate';
import {FormattedCurrency} from '@ui/i18n/formatted-currency';
import {useAuth} from '@common/auth/use-auth';
import {consumeAuthRedirectUri} from '@common/auth/auth-redirect-uri';
import {getMusicPreferenceRedirectUri} from '@app/web-player/onboarding/music-preference/redirect';

export function RegisterPage() {
  const {
    branding,
    registration: {disable},
    social,
  } = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;
  const referrerCode = searchParams.get('ref') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {
      email: searchParamsEmail,
      referrer_code: referrerCode,
    },
  });
  const navigate = useNavigate();
  const {getRedirectUri} = useAuth();
  const register = useRegister(form, {
    onRegistered: async () => {
      // After register, if voucher is 100%, create free subscription and redirect to success
      if (voucherPreview?.valid && voucherPreview.voucher && voucherPreview.plan) {
        const percent = voucherPreview.voucher.discount_percentage;
        try {
          if (percent === 100) {
            // create free subscription immediately
            await fetchFreeSubscription(voucherPreview);
            window.location.href = getMusicPreferenceRedirectUri(
              '/checkout/voucher/success',
            );
            return true; // handled navigation
          } else {
            // redirect user to checkout of that plan with hint to apply code
            toast.positive('Voucher válido! Aplique o desconto na próxima etapa.');
            navigate(`/checkout/${voucherPreview.plan.product_id}/${voucherPreview.plan.price_id}?voucher=${encodeURIComponent(voucherPreview.voucher.code)}`);
            return true; // handled navigation
          }
        } catch (e: any) {
          toast.danger(e?.response?.data?.message || 'Erro ao aplicar voucher após cadastro');
          return false; // fall back to default navigation
        }
      }
      // No voucher applied on register page: do NOT send user to checkout
      // Handle navigation explicitly to home (or another safe default)
      navigate(
        getMusicPreferenceRedirectUri(
          consumeAuthRedirectUri() || getRedirectUri(),
        ),
        {replace: true},
      );
      return true; // prevent default navigation that could use onboarding -> checkout
    },
  });
  const previewVoucher = usePreviewRegisterVoucher();
  const [isVoucherExpanded, setIsVoucherExpanded] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherPreview, setVoucherPreview] = useState<PreviewRegisterVoucherResponse | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  if (disable) {
    return <Navigate to="/login" replace />;
  }

async function fetchFreeSubscription(preview: PreviewRegisterVoucherResponse) {
  // Defensive checks
  if (!preview.voucher || !preview.plan) return;
  const {voucher, plan} = preview;
  // Use same endpoint as checkout for free vouchers
  const res = await fetch('/api/v1/vouchers/create-free-subscription', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      product_id: plan.product_id,
      price_id: plan.price_id,
      voucher_id: voucher.id,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.message || 'Erro ao criar assinatura gratuita');
  }
}

function handleApplyVoucher() {
  const code = voucherCode.trim().toUpperCase();
  if (!code) return;
  previewVoucher.mutate(
    {voucher_code: code},
    {
      onSuccess: data => {
        setVoucherPreview(data);
        if (data.valid) {
          toast.positive('Voucher válido!');
        } else {
          toast.danger(data.message || 'Voucher inválido.');
        }
      },
      onError: (error: any) => {
        const message = error?.response?.data?.message || 'Erro ao validar voucher';
        setVoucherPreview(null);
        toast.danger(message);
      },
    }
  );
}

  let heading = <Trans message="Create a new account" />;
  if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  } else if (referrerCode) {
    heading = <Trans message="Create an account - Referred by a friend" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/login">
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          const isValid = await verify();
          if (isValid) {
            register.mutate(payload);
          }
        }}
      >
        <FormTextField
          className="mb-32"
          name="email"
          type="email"
          disabled={!!searchParamsEmail}
          label={<Trans message="Email" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="username"
          type="text"
          label={<Trans message="Nome de Usuário" />}
          required
        />
        <FormTextField
          className="mb-32"
          name="phone"
          type="text"
          label={<Trans message="Número de Telefone" />}
          pattern="[0-9 +()-]{10,20}"
        />
        <FormTextField
          className="mb-32"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={<Trans message="Password" />}
          required
          endAdornment={
            <div className="pointer-events-auto pr-6">
              <IconButton
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPassword(v => !v)}
                type="button"
                variant="text"
                size="sm"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </div>
          }
        />
        <FormTextField
          className="mb-32"
          name="password_confirmation"
          type={showPasswordConfirm ? 'text' : 'password'}
          label={<Trans message="Confirm password" />}
          required
          endAdornment={
            <div className="pointer-events-auto pr-6">
              <IconButton
                aria-label={
                  showPasswordConfirm
                    ? 'Ocultar confirmação'
                    : 'Mostrar confirmação'
                }
                onMouseDown={e => e.preventDefault()}
                onClick={() => setShowPasswordConfirm(v => !v)}
                type="button"
                variant="text"
                size="sm"
              >
                {showPasswordConfirm ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </IconButton>
            </div>
          }
        />
        {/* Voucher toggle and input (moved below password fields) */}
        <div className="mb-16 mt-8">
          <button
            type="button"
            onClick={() => setIsVoucherExpanded(v => !v)}
            className="flex w-full items-center justify-between rounded-lg border border-divider bg-background p-12 text-left transition-colors hover:bg-hover"
          >
            <div className="flex items-center gap-10">
              <AirplaneTicketIcon className="text-primary" size="sm" />
              <span className="text-sm font-medium">
                <Trans message="Tem cupom de desconto?" />
              </span>
            </div>
            {isVoucherExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </button>

          {isVoucherExpanded && (
            <div className="mt-12 flex items-center gap-8">
              <TextField
                placeholder="Código do voucher"
                value={voucherCode}
                onChange={e => setVoucherCode(e.target.value)}
                className="flex-1"
                onKeyDown={e => {
                  if (e.key === 'Enter') handleApplyVoucher();
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleApplyVoucher}
                disabled={previewVoucher.isPending || !voucherCode.trim()}
              >
                {previewVoucher.isPending ? <Trans message="Validando..." /> : <Trans message="Aplicar" />}
              </Button>
            </div>
          )}

          {voucherPreview && (
            <div
              className={`mt-12 rounded-lg border p-12 ${voucherPreview.valid ? 'border-positive bg-positive/5' : 'border-danger bg-danger/5'}`}
            >
              <div className="text-sm">
                {voucherPreview.message}
              </div>
              {voucherPreview.valid && (
                <div className="mt-10 space-y-12 text-xs text-muted">
                  <div>
                    <Trans message="Plano" />: {voucherPreview.plan?.product_name} (
                    {voucherPreview.plan?.interval})
                  </div>
                  <div>
                    <Trans message="Desconto" />: {voucherPreview.voucher?.discount_percentage}%
                  </div>
                  {voucherPreview.pricing && (
                    <div className="space-y-8 rounded-lg bg-background/60 p-12 text-sm text-foreground">
                      <div className="flex items-center justify-between gap-24 text-muted">
                        <div>
                          <Trans message="Original price" />
                        </div>
                        <div className="line-through">
                          <FormattedCurrency
                            value={voucherPreview.pricing.original_price}
                            currency={voucherPreview.pricing.currency || 'BRL'}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-24 text-positive">
                        <div>
                          <Trans message="Discount" /> ({voucherPreview.pricing.discount_percentage}%)
                        </div>
                        <div>
                          -
                          <FormattedCurrency
                            value={voucherPreview.pricing.discount_amount}
                            currency={voucherPreview.pricing.currency || 'BRL'}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-24 font-medium text-lg">
                        <div>
                          <Trans
                            message={
                              voucherPreview.pricing.final_price === 0
                                ? 'Total (Free)'
                                : 'Billed today'
                            }
                          />
                        </div>
                        <div
                          className={
                            voucherPreview.pricing.final_price === 0 ? 'text-positive' : undefined
                          }
                        >
                          {voucherPreview.pricing.final_price === 0 ? (
                            <Trans message="FREE" />
                          ) : (
                            <FormattedCurrency
                              value={voucherPreview.pricing.final_price}
                              currency={voucherPreview.pricing.currency || 'BRL'}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {(() => {
          const RegisterFields = (auth as any)?.registerFields;
          return RegisterFields ? <RegisterFields /> : null;
        })()}
        <PolicyCheckboxes />
        <Button
          className="block w-full"
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          disabled={register.isPending || isVerifying}
        >
          <Trans message="Create account" />
        </Button>
        <SocialAuthSection
          dividerMessage={
            social.compact_buttons ? (
              <Trans message="Or sign up with" />
            ) : (
              <Trans message="OR" />
            )
          }
        />
      </Form>
    </AuthLayout>
  );
}

function PolicyCheckboxes() {
  const {
    registration: {policies},
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-32">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
